import { useState, useEffect } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ScrollToTop from '@/components/base/ScrollToTop';
import SEOHead from '@/components/feature/SEOHead';
import { categories } from '@/mocks/categories';
import { articles } from '@/mocks/articles';
import { siteInfo } from '@/mocks/siteInfo';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const [activeSlug, setActiveSlug] = useState(slug || 'platform-guide');

  useEffect(() => {
    if (slug) {
      setActiveSlug(slug);
    }
  }, [slug]);

  const currentCategory = categories.find((c) => c.slug === activeSlug) || categories[0];
  const categoryArticles = articles.filter((a) => a.category === activeSlug);

  const handleCategoryChange = (newSlug: string) => {
    setActiveSlug(newSlug);
    navigate(`/category/${newSlug}`);
  };

  return (
    <>
      <ScrollToTop />
      <SEOHead
        title={`${currentCategory.name} - 凪光在线 | ${currentCategory.description}`}
        description={currentCategory.description}
        canonicalUrl={`https://naguflow.github.io/category/${activeSlug}`}
        jsonLd={{
          '@context': 'https://schema.org',
          '@type': 'CollectionPage',
          name: `${currentCategory.name} - 凪光在线`,
          description: currentCategory.description,
          url: `https://naguflow.github.io/category/${activeSlug}`,
        }}
      />

      <Navbar />

      <main className="pt-20">
        <Breadcrumb
          items={[
            { label: '分类', path: '/category/platform-guide' },
            { label: currentCategory.name },
          ]}
        />

        <section className="py-10 md:py-16 bg-background-50">
          <div className="max-w-[1280px] mx-auto px-4 md:px-6">
            <div className="mb-10">
              <h1 className="text-2xl md:text-4xl font-heading font-semibold text-foreground-950 mb-3">
                {currentCategory.name}
              </h1>
              <p className="text-sm text-foreground-600">{currentCategory.description}</p>
            </div>

            <div className="mb-10 overflow-x-auto">
              <div className="flex items-center gap-2 flex-nowrap min-w-max">
                {categories.map((cat) => (
                  <button
                    key={cat.slug}
                    className={`px-5 py-2 rounded-full text-sm font-medium transition-all duration-200 whitespace-nowrap cursor-pointer ${
                      activeSlug === cat.slug
                        ? 'bg-primary-500 text-background-50'
                        : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                    }`}
                    onClick={() => handleCategoryChange(cat.slug)}
                  >
                    <i className={`${cat.icon} mr-1.5`}></i>
                    {cat.name}
                  </button>
                ))}
              </div>
            </div>

            {categoryArticles.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {categoryArticles.map((article) => (
                  <Link
                    key={article.slug}
                    to={`/article/${article.slug}`}
                    className="group bg-background-50 rounded-xl overflow-hidden border border-background-200/70 hover:border-primary-200 transition-all duration-300 cursor-pointer"
                  >
                    <div className="aspect-[16/9] overflow-hidden">
                      <img
                        src={article.coverImage}
                        alt={article.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                    </div>
                    <div className="p-5">
                      <div className="flex items-center gap-3 mb-3 text-xs text-foreground-500">
                        <span className="flex items-center gap-1">
                          <i className="ri-calendar-line"></i>
                          {article.publishDate}
                        </span>
                        <span className="flex items-center gap-1">
                          <i className="ri-time-line"></i>
                          {article.readTime}
                        </span>
                      </div>
                      <h3 className="text-base font-heading font-semibold text-foreground-950 mb-2 group-hover:text-primary-600 transition-colors line-clamp-2">
                        {article.title}
                      </h3>
                      <p className="text-sm text-foreground-600 leading-relaxed line-clamp-2">
                        {article.summary}
                      </p>
                      <div className="mt-3 flex items-center gap-2 flex-wrap">
                        {article.tags.slice(0, 3).map((tag) => (
                          <span
                            key={tag}
                            className="px-2 py-0.5 bg-background-100 text-foreground-500 text-xs rounded"
                          >
                            {tag}
                          </span>
                        ))}
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            ) : (
              <div className="text-center py-20">
                <div className="w-16 h-16 rounded-full bg-background-200 flex items-center justify-center mx-auto mb-4">
                  <i className="ri-inbox-line text-2xl text-foreground-400"></i>
                </div>
                <p className="text-foreground-500 text-sm">该分类暂无文章</p>
              </div>
            )}
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}